import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Image, Text, useStyleConfig, useMultiStyleConfig} from '@chakra-ui/react'
import loadable from '@loadable/component'
import {useIntl} from 'react-intl'
import Link from '@salesforce/retail-react-app/app/components/link'
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useProductData from '../../../../hooks/use-product-data'
import {useBooleanCustomPreference} from '../../../../hooks/use-custom-preference'
import ProductTileImage from '../../../product-tile/partials/product-tile-image'
import PriceSection from '../../../product-tile/partials/price-section'
import SwatchSection from '../../../product-tile/partials/swatch-section'
import CurrentPrice from '../../../display-price/current-price'
import SanitizedHtml from '../../../html-sanitizer'
import {AddToCartIcon, CloudIcon, BeanIcon, SizeIcon} from '../../../icons'
import EnergyLabel from '../../../energy-label'
import {CARD_VARIANTS} from '../utils'
import {remCalc} from '../../../../theme/_utils'

const ReviewScoreIcon = loadable(() => import('../../../review/ReviewScoreIcon'))

const getReviewIds = (product) => ({
    bazaarId:
        product?.c_bundleType === 'Flexible'
            ? product?.c_firstPrimaryProductId
            : product?.representedProduct?.c_subModelIdentifier ||
              product?.productId ||
              product?.id,
    feefoId:
        product?.c_bundleType === 'Flexible'
            ? product?.c_primaryProductMasterId
            : product?.c_prdha_category || product?.productId || product?.id,
})

const ProductCard = ({variant, products, characteristics = []}) => {
    const styles = useStyleConfig('CarouselCards')
    const [isHovered, setIsHovered] = useState(false)
    const tileStyles = useMultiStyleConfig('ProductTile')
    const intl = useIntl()
    const {currency} = useCurrency()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const primaryProduct = products[0]
    const {productUrl, priceData} = useProductData(
        primaryProduct,
        PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID,
    )

    const isBundle = variant === CARD_VARIANTS.BUNDLE
    const isComparison = variant === CARD_VARIANTS.COMPARISON
    const {bazaarId, feefoId} = getReviewIds(primaryProduct)
    const hasSwatches = !isBundle && primaryProduct?.c_enabledVariationGroups?.length > 0
    const [hasVisibleReview, setHasVisibleReview] = useState(false)
    const shouldReserveReviewSwatchSpace = !hasVisibleReview && !hasSwatches

    useEffect(() => {
        setHasVisibleReview(false)
    }, [bazaarId, feefoId])

    const isOutOfStock =
        primaryProduct?.inventory?.orderable === false ||
        primaryProduct?.inventory?.stockLevel === 0
    const hasPricing =
        Number.isFinite(primaryProduct?.price) && Number.isFinite(priceData?.currentPrice)
    const showTransactionalBlock = !notShoppableCountry && hasPricing && !isOutOfStock
    const productName =
        primaryProduct?.c_variantIdentifier || primaryProduct?.name || primaryProduct?.productName
    const featureProduct = primaryProduct?.representedProduct || primaryProduct
    const inlineFeatures = [
        {icon: CloudIcon, title: featureProduct?.c_PLPkeyFeature1},
        {icon: BeanIcon, title: featureProduct?.c_PLPkeyFeature2},
        {icon: SizeIcon, title: featureProduct?.c_PLPkeyFeature3},
    ].filter((feature) => feature.title)
    const actionLabel = intl.formatMessage({
        id: 'carousel_cards.button.discover_more',
        defaultMessage: 'Discover more',
    })
    const shopLabel = intl.formatMessage({
        id: 'carousel_cards.button.shop_now',
        defaultMessage: 'Shop now',
    })

    const energyClass =
        primaryProduct?.representedProduct?.c_energyEfficiencyClass ||
        primaryProduct?.c_energyEfficiencyClass

    return (
        <Flex
            {...styles?.card}
            data-testid={`carousel-cards-${variant}-card`}
            data-card-variant={variant}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Box {...styles?.productMedia}>
                <ProductTileImage
                    product={primaryProduct}
                    productUrl={productUrl}
                    priceData={priceData}
                    isHovered={isHovered}
                    showOutOfStock={isOutOfStock}
                    isInformativeLabelActive={!isComparison}
                    imageStyle={{width: '100%', height: '100%', objectFit: 'cover'}}
                    viewType="large"
                />
            </Box>

            <Flex
                {...tileStyles.bottomContainer}
                {...styles?.productInfo}
                flex="1"
                justifyContent="flex-start"
            >
                {!isComparison && (
                    <Flex {...tileStyles.headerRow} justifyContent="space-between">
                        <Flex
                            {...tileStyles.subheaderRow}
                            minHeight={shouldReserveReviewSwatchSpace ? remCalc(24) : undefined}
                        >
                            <ReviewScoreIcon
                                id={bazaarId}
                                parentSku={feefoId}
                                variant={'plpReview'}
                                onVisibilityChange={setHasVisibleReview}
                            />
                            {!isBundle && (
                                <SwatchSection
                                    swatchInfo={primaryProduct?.c_enabledVariationGroups}
                                />
                            )}
                        </Flex>
                        <Flex maxWidth={remCalc(42)} maxHeight={remCalc(22)}>
                            {energyClass && (
                                <Box {...tileStyles.energyLabel}>
                                    <EnergyLabel
                                        level={energyClass}
                                        labelPosition="right"
                                        isPlp={true}
                                        manualDocument={
                                            primaryProduct?.c_manualDocument ||
                                            primaryProduct?.representedProduct?.c_manualDocument
                                        }
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                )}

                <Flex width="100%" flex="1" flexDir="column" {...styles?.productContent}>
                    <Flex {...tileStyles.body} {...styles?.productBody}>
                        <Flex width="100%" {...styles?.productNameContainer}>
                            <Text
                                as="h3"
                                {...tileStyles.title}
                                {...styles?.productName}
                                noOfLines={undefined}
                                overflow="visible"
                                overflowWrap="anywhere"
                                width="100%"
                            >
                                <Link to={productUrl} color="inherit" textDecoration="none">
                                    {productName}
                                </Link>
                            </Text>
                        </Flex>
                        {isComparison && primaryProduct?.longDescription && (
                            <SanitizedHtml
                                as={Box}
                                html={primaryProduct.longDescription}
                                sanitize
                                {...tileStyles.featureListElement}
                                color="foreground.neutral.muted"
                                textStyle="body-small"
                                data-testid="carousel-cards-comparison-description"
                            />
                        )}
                        {!isComparison && !isBundle && inlineFeatures.length > 0 && (
                            <Flex
                                {...tileStyles.keyFeaturesContainer}
                                {...styles?.keyFeaturesContainer}
                                maxWidth="100%"
                            >
                                <Flex {...tileStyles.keyFeature} maxWidth="100%">
                                    {inlineFeatures.map((feature, index) => {
                                        const FeatureIcon = feature.icon
                                        return (
                                            <Flex key={index} {...tileStyles.keyFeatureElement}>
                                                <FeatureIcon boxSize="14px" />
                                                <Text
                                                    {...tileStyles.featureListElement}
                                                    {...styles?.keyFeatureText}
                                                    // noOfLines={2}
                                                >
                                                    {feature.title}
                                                </Text>
                                            </Flex>
                                        )
                                    })}
                                </Flex>
                            </Flex>
                        )}
                    </Flex>

                    {variant === CARD_VARIANTS.COMPARISON && characteristics.length > 0 && (
                        <Flex as="ul" {...styles?.characteristicsList}>
                            {characteristics.map((characteristic, index) => (
                                <Flex
                                    as="li"
                                    key={characteristic?._metadata?.uid || index}
                                    {...styles?.characteristicsItem}
                                >
                                    {characteristic?.icon?.url && (
                                        <Image
                                            src={characteristic?.icon?.url}
                                            alt=""
                                            aria-hidden="true"
                                            {...styles?.characteristicsIcon}
                                        />
                                    )}
                                    <Text as="span" {...tileStyles.featureListElement}>
                                        {characteristic?.title}
                                    </Text>
                                </Flex>
                            ))}
                        </Flex>
                    )}
                </Flex>

                <Flex {...tileStyles.footerRow} {...styles?.productFooterRow}>
                    {isComparison && !notShoppableCountry && hasPricing && (
                        <CurrentPrice
                            price={priceData.currentPrice}
                            currency={primaryProduct.currency || currency}
                            isRange
                            as="p"
                            {...styles?.comparisonPrice}
                            rangePriceProps={styles?.comparisonPriceValue || {}}
                        />
                    )}
                    {!isComparison && !notShoppableCountry && hasPricing && (
                        <PriceSection
                            priceData={priceData}
                            currency={currency}
                            notShoppableCountry={notShoppableCountry}
                            variant="editorial"
                        />
                    )}
                    {productUrl && (
                        <Button
                            as={Link}
                            to={productUrl}
                            aria-label={showTransactionalBlock ? shopLabel : actionLabel}
                            variant={isHovered ? 'icon-button-primary' : 'icon-button-outline'}
                            data-button-variant={
                                isHovered ? 'icon-button-primary' : 'icon-button-outline'
                            }
                            size="small"
                            leftIcon={
                                <AddToCartIcon
                                    width={remCalc(16)}
                                    height={remCalc(16)}
                                    color={
                                        isHovered
                                            ? 'foreground.neutral.inverse'
                                            : 'foreground.neutral.default'
                                    }
                                    fill="none"
                                />
                            }
                            paddingX={remCalc(10)}
                            paddingY={remCalc(10)}
                            {...styles?.cardCta}
                        />
                    )}
                </Flex>
            </Flex>
        </Flex>
    )
}

ProductCard.propTypes = {
    variant: PropTypes.string.isRequired,
    products: PropTypes.array.isRequired,
    characteristics: PropTypes.array,
}

export default ProductCard
